<template>
  <ConfirmationModalRoot v-model="isOpen" prevent-close-on-backdrop-click>
    <template #default>
      <ConfirmationModalTitle>
        {{ t("channel.signup.ageGate.title") }}
      </ConfirmationModalTitle>

      <div class="w-full px-2">
        <div
          class="grow px-4 py-2 border-brand-tertiary-60 border rounded-lg bg-surface text-basics-primary text-base w-full"
        >
          <select
            v-model="selectedYear"
            class="font-light ring-0 outline-0 w-full bg-transparent"
          >
            <option value="" disabled>
              {{ t("channel.signup.ageGate.placeholder") }}
            </option>
            <option v-for="year in years" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>

        <p v-if="error" role="alert" class="text-red-500 text-sm mt-2">
          {{ error }}
        </p>
      </div>
    </template>

    <template #actions>
      <ButtonV2 variant="primary" size="sm" @click="confirm">
        {{ t("channel.signup.ageGate.confirm") }}
      </ButtonV2>
      <ButtonV2 variant="subtle" size="sm" @click="cancel">
        {{ t("channel.modal.cancel") }}
      </ButtonV2>
    </template>
  </ConfirmationModalRoot>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import ConfirmationModalRoot from "@/components/ui/modal/confirmation-modal/confirmation-modal-root.vue";
import ConfirmationModalTitle from "@/components/ui/modal/confirmation-modal/confirmation-modal-title.vue";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { getAge } from "@/utils/age";
import { useDumbStorage } from "@/composables/use-dumb-storage";

const ONE_HOUR_MS = 60 * 60 * 1000;

interface AgeGateBlockedData {
  timestamp: number;
}

const isOpen = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  confirm: [year: string];
  cancel: [];
}>();

const { t } = useI18n();

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 120 }, (_, i) => currentYear - i);

const selectedYear = ref<string>("");
const error = ref("");

const ageGateBlocked = useDumbStorage<AgeGateBlockedData | null>(
  "f3_age_gate_blocked",
  null,
  {
    serializer: {
      read(raw: string): AgeGateBlockedData | null {
        try {
          const data = JSON.parse(raw) as AgeGateBlockedData;
          if (Date.now() - data.timestamp >= ONE_HOUR_MS) return null;
          return data;
        } catch {
          return null;
        }
      },
      write(value: AgeGateBlockedData | null): string | null {
        return value ? JSON.stringify(value) : null;
      },
    },
  },
);

function confirm() {
  error.value = "";

  if (!selectedYear.value) {
    error.value = t("channel.signup.ageGate.selectYear");
    return;
  }

  // Check if previously blocked (expires after 1 year)
  if (ageGateBlocked.value !== null) {
    error.value = t("channel.signup.ageGate.error");
    return;
  }

  const dob = new Date(parseInt(selectedYear.value, 10), 0, 1);
  const age = getAge(dob);

  if (age !== null && age < 13) {
    ageGateBlocked.value = { timestamp: Date.now() };
    error.value = t("channel.signup.ageGate.error");
    return;
  }

  emit("confirm", selectedYear.value);
  isOpen.value = false;
}

function cancel() {
  emit("cancel");
  isOpen.value = false;
}
</script>
